import type { ProviderSessionType } from '@/shared/api/graphql/remotePatientMonitoring';
import type { CcmAutoEnrollmentFields, GeneralUpdateResponseType } from '@/shared/lib/types/graphql';
import type {
  RpmAccountHealthTrackingType,
  RpmAccountSummaryType,
  RpmFollowupConsultTabType
} from '@/shared/lib/types/remotePatientMonitoring';
import { gql } from '@apollo/client';
export enum AccountCareSummaryStatus {
  Requested = 'Requested',
  Available = 'Available',
  Unavailable = 'Unavailable',
  Pending = 'Pending'
}

// Mirrors AccountCareSummaryAiStatus on the backend, which is a plain String field rather than a
// GraphQL enum. Deliberately separate from AccountCareSummaryStatus: that tracks whether Connective
// Health delivered the record, this tracks whether we managed to summarize it. A record can be
// Available while its summary is still Pending, Failed or Unavailable.
export enum AccountCareSummaryAiStatus {
  Pending = 'PENDING',
  Available = 'AVAILABLE',
  Unavailable = 'UNAVAILABLE',
  Failed = 'FAILED'
}

export type AccountCareSummaryRequest = {
  id: string;
  externalCorrelationId: string;
  s3Key: string;
  jsonS3Key: string;
  source: string;
  status: AccountCareSummaryStatus;
  requestedOn: string;
  importedOn: string;
  submittedForReviewOn: string;
  // Null until generation has run -- read aiSummaryStatus to tell "not ready yet" from "nothing to say".
  aiSummary: string | null;
  aiSummaryStatus: AccountCareSummaryAiStatus | null;
  aiSummaryGeneratedAt: string | null;
  createdAt: string;
  updatedAt: string;
};

export type GetAccountCareSummariesResponse = {
  accountCareSummaries: {
    rpmEncounterS3Key?: string;
    carePlanS3Key?: string;
    accountCareSummaries: AccountCareSummaryRequest[];
    meta: {
      totalPages: number;
      totalCount: number;
    };
  };
};

export const GET_ACCOUNT_CARE_SUMMARIES = gql`
  query GetAccountCareSummariesQuery(
    $truentityId: String!
    $filterOptions: AccountCareSummariesFilterOptionsInput
    $pageNum: Int!
    $pageSize: Int!
  ) {
    accountCareSummaries(truentityId: $truentityId, filterOptions: $filterOptions, pageNum: $pageNum, pageSize: $pageSize) {
      accountCareSummaries {
        id
        externalCorrelationId
        s3Key
        jsonS3Key
        source
        status
        requestedOn
        importedOn
        submittedForReviewOn
        aiSummary
        aiSummaryStatus
        aiSummaryGeneratedAt
        createdAt
        updatedAt
      }
      meta {
        totalPages
        totalCount
      }
    }
  }
`;

export type ImportAccountCareSummaryResponse = {
  importAccountCareSummary: {
    status: string;
    message: string;
  };
};

export const IMPORT_ACCOUNT_CARE_SUMMARY = gql`
  mutation ImportAccountCareSummary(
    $truentityId: String!
    $appointmentDate: String!
    $rpmFollowupConsultId: String
    $clinicalCareEncounterId: String
  ) {
    importAccountCareSummary(
      truentityId: $truentityId
      appointmentDate: $appointmentDate
      rpmFollowupConsultId: $rpmFollowupConsultId
      clinicalCareEncounterId: $clinicalCareEncounterId
    ) {
      status
      message
    }
  }
`;

export type GenerateAiSummaryResponse = {
  generateAiSummary: {
    status: string;
    message: string;
    // The record's state after the call. On a second click this comes back as whatever the first
    // one left behind rather than Pending, which is how the caller learns nothing new was started.
    aiSummaryStatus: AccountCareSummaryAiStatus | null;
  };
};

// Records delivered since the S3 upload trigger went live are summarized automatically and never
// need this. It exists for records that predate it: those bundles are already sitting in S3, so no
// upload event will ever fire for them and the note only gets written if someone asks.
//
// Every call is one LLM invocation against a full clinical bundle, so it is deliberately per-record
// and provider-initiated. The backend refuses to spend a second call on a record that already has a
// note or has one in flight -- it answers Success with the existing status instead of erroring.
export const GENERATE_AI_SUMMARY = gql`
  mutation GenerateAiSummary($truentityId: String!, $accountCareSummaryId: String!) {
    generateAiSummary(truentityId: $truentityId, accountCareSummaryId: $accountCareSummaryId) {
      status
      message
      aiSummaryStatus
    }
  }
`;

export const REGENERATE_ACCOUNT_CARE_PLAN_REPORT = gql`
  mutation RegenerateCarePlanReport($truentityId: ID!, $rpmFollowupConsultId: String, $clinicalCareEncounterId: String) {
    regenerateCarePlanReport(
      truentityId: $truentityId
      rpmFollowupConsultId: $rpmFollowupConsultId
      clinicalCareEncounterId: $clinicalCareEncounterId
    ) {
      status
      message
    }
  }
`;

export type SubmitAccountCareSummaryForReviewResponse = {
  reviewAccountCareSummary: {
    status: string;
    message: string;
    providerSession: ProviderSessionType | null;
  };
};

export const SUBMIT_ACCOUNT_CARE_SUMMARY_FOR_REVIEW = gql`
  mutation ReviewAccountCareSummary(
    $truentityId: String!
    $accountCareSummaryIds: [String!]!
    $isRpmEncounterSubmitted: Boolean!
    $reviewSubmitType: ReviewSubmitTypeEnum!
  ) {
    reviewAccountCareSummary(
      truentityId: $truentityId
      accountCareSummaryIds: $accountCareSummaryIds
      isRpmEncounterSubmitted: $isRpmEncounterSubmitted
      reviewSubmitType: $reviewSubmitType
    ) {
      status
      message
      providerSession {
        status
        sessionId
      }
    }
  }
`;

export type GetDocumentPreviewResponse = {
  documentPreview: {
    documentS3Key: string;
    documentUrl: string;
    documentType: string;
  };
};

export type GetConnectiveJsonPreviewResponse = {
  connectiveJsonPreview: {
    documentS3Key: string;
    documentUrl: string;
    documentType: string;
  };
};

export const GET_DOCUMENT_PREVIEW = gql`
  query GetDocumentPreview($documentS3Key: String!) {
    documentPreview(documentS3Key: $documentS3Key) {
      documentS3Key
      documentUrl
      documentType
    }
  }
`;

export const CONNECTIVE_JSON_PREVIEW = gql`
  query ConnectiveJsonPreview($jsonS3Key: String!) {
    connectiveJsonPreview(jsonS3Key: $jsonS3Key) {
      documentS3Key
      documentUrl
      documentType
    }
  }
`;

export type CreatePdfRpmEncounterReportResponse = {
  createPdfRpmAccountEncounterReport: {
    pdfDownloadUrl: string;
    status: string;
    errors: string[];
  };
};

export const CREATE_PDF_RPM_ENCOUNTER_REPORT = gql`
  mutation CreatePdfRpmAccountEncounterReport($truentityId: String!) {
    createPdfRpmAccountEncounterReport(truentityId: $truentityId) {
      pdfDownloadUrl
      status
      errors
    }
  }
`;

export type GetRpmHealthTrackingsResponse = {
  getAccountHealthTrackings: {
    healthTrackings: RpmAccountHealthTrackingType[];
    meta: {
      totalPages: number;
      totalCount: number;
    };
  };
};

export const GET_RPM_HEALTH_TRACKINGS = gql`
  query GetAccountHealthTrackings($filterOptions: HealthTrackingsFilterOptionsInput, $truentityId: ID!, $pageNum: Int!, $pageSize: Int!) {
    getAccountHealthTrackings(truentityId: $truentityId, pageNum: $pageNum, pageSize: $pageSize, filterOptions: $filterOptions) {
      healthTrackings {
        id
        account {
          user {
            firstName
            lastName
          }
        }
        value
        unit
        key
        startedAt
        endedAt
        sourceType
      }
      meta {
        totalPages
        totalCount
      }
    }
  }
`;

export type GetRpmAccountSummaryResponse = {
  getRpmAccountSummary: RpmAccountSummaryType;
};

export const GET_RPM_ACCOUNT_SUMMARY = gql`
  query GetRpmAccountSummary($truentityId: String!) {
    getRpmAccountSummary(truentityId: $truentityId) {
      carePlanS3Key
      rpmEncounterS3Key
      signOffAt
      isCarePlanFilled
      amendable
      amendmentStatus
      lastAmendedAt
      amendmentCount
    }
  }
`;

export type UpdateRpmFollowupConsultResponse = {
  updateRpmFollowupConsult: GeneralUpdateResponseType & CcmAutoEnrollmentFields;
};

export const UPDATE_RPM_FOLLOWUP_CONSULT = gql`
  mutation UpdateRpmFollowupConsult($id: ID!, $rpmFollowupConsultInput: UpdateRpmFollowupConsultInput!) {
    updateRpmFollowupConsult(id: $id, rpmFollowupConsultInput: $rpmFollowupConsultInput) {
      message
      status
      ccmEnrolled
      ccmEnrollmentMessage
    }
  }
`;

export type AmendClinicalCareEncounterResponse = {
  amendClinicalCareEncounter: GeneralUpdateResponseType & CcmAutoEnrollmentFields;
};

export const AMEND_CLINICAL_CARE_ENCOUNTER = gql`
  mutation AmendClinicalCareEncounter($truentityId: ID!, $amendClinicalCareEncounterInput: AmendClinicalCareEncounterInput!) {
    amendClinicalCareEncounter(truentityId: $truentityId, amendClinicalCareEncounterInput: $amendClinicalCareEncounterInput) {
      message
      status
      ccmEnrolled
      ccmEnrollmentMessage
    }
  }
`;

export type ArchiveRpmFollowupConsultResponse = {
  archiveRpmFollowupConsult: GeneralUpdateResponseType;
};

export const ARCHIVE_RPM_FOLLOWUP_CONSULT = gql`
  mutation ArchiveRpmFollowupConsult($id: ID!, $sendTextReminder: Boolean, $textReminderMessage: String) {
    archiveRpmFollowupConsult(id: $id, sendTextReminder: $sendTextReminder, textReminderMessage: $textReminderMessage) {
      message
      status
    }
  }
`;

export type UpdateRpmFollowupConsultTabsTabsResponse = {
  updateRpmFollowupConsultTab: GeneralUpdateResponseType;
};

export const UPDATE_RPM_FOLLOWUP_CONSULT_TAB = gql`
  mutation UpdateRpmFollowupConsultTab($id: ID!, $status: String, $isManualStatusUpdate: Boolean) {
    updateRpmFollowupConsultTab(id: $id, status: $status, isManualStatusUpdate: $isManualStatusUpdate) {
      status
      message
    }
  }
`;

export type GetRpmFollowupConsultTabsResponse = {
  getRpmFollowupConsultTabs: {
    rpmFollowupConsultTabs: RpmFollowupConsultTabType[];
  };
};

export const GET_RPM_FOLLOWUP_CONSULT_TABS = gql`
  query GetRpmFollowupConsultTabs($rpmFollowupConsultId: String!, $rpmFollowupConsultTabTypes: [RpmSetupTabsTypeEnum!]!) {
    getRpmFollowupConsultTabs(rpmFollowupConsultId: $rpmFollowupConsultId, rpmFollowupConsultTabTypes: $rpmFollowupConsultTabTypes) {
      rpmFollowupConsultTabs {
        id
        type
        status
        isManualStatusUpdate
        createdAt
        updatedAt
      }
    }
  }
`;

export type CreatePdfRpmFollowupConsultReport = {
  createPdfRpmFollowupConsultReport: {
    pdfDownloadUrl: string;
    status: string;
  };
};

export const CREATE_PDF_RPM_FOLLOWUP_CONSULT_REPORT = gql`
  mutation CreatePdfRpmFollowupConsultReport($rpmFollowupConsultId: ID!, $reportType: String) {
    createPdfRpmFollowupConsultReport(rpmFollowupConsultId: $rpmFollowupConsultId, reportType: $reportType) {
      pdfDownloadUrl
      status
    }
  }
`;

export type SendMeetUrlTextMessage = {
  sendMeetUrlTextMessage: {
    status: string;
  };
};

export const SEND_MEET_URL_TEXT_MESSAGE = gql`
  mutation SendMeetUrlTextMessage($meetUrl: String!, $accountUsername: String!, $toPhoneNumber: String!) {
    sendMeetUrlTextMessage(meetUrl: $meetUrl, accountUsername: $accountUsername, toPhoneNumber: $toPhoneNumber) {
      status
      message
    }
  }
`;
